import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  listPermissions,
  createPermission,
  updatePermission,
} from "../../../../utils/services/control/rbacAdminService";
import CatalogFormDialog from "./CatalogFormDialog";
import useSnack from "../../hooks/useSnack";

const FORM_LABELS = {
  entityName: "Permission",
  nameLabel: "Permission Name",
  keyLabel: "Permission Key",
  keyHelper: "Stable key — auto-normalized. Cannot be changed later.",
  errName: "Permission name is required",
  errKey: "Permission key is required",
};

const PermissionsCatalogTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editEntity, setEditEntity] = useState(null);
  const [saving, setSaving] = useState(false);

  const { snack, showSnack, closeSnack } = useSnack();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Solo se listan activos: los permisos no se pueden desactivar.
      const data = await listPermissions(1);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading permissions:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async ({ name, key, description }) => {
    setSaving(true);
    try {
      const res = editEntity
        ? await updatePermission({
            permissionId: editEntity.permissionId,
            permissionName: name,
            description,
          })
        : await createPermission({
            permissionName: name,
            permissionKey: key,
            description,
          });
      if (res.result !== 0) {
        showSnack(res.message, "error");
      } else {
        showSnack(
          editEntity
            ? "Permission updated successfully"
            : "Permission created successfully"
        );
        setFormOpen(false);
        setEditEntity(null);
        loadData();
      }
    } catch (err) {
      showSnack(
        err.response?.data?.message || "Error saving permission",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      field: "permissionName",
      headerName: "Permission Name",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "permissionKey",
      headerName: "Permission Key",
      width: 160,
      renderCell: ({ value }) => (
        <Typography variant="caption" fontFamily="monospace">
          {value}
        </Typography>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
      minWidth: 200,
      renderCell: ({ value }) => (
        <Typography variant="body2" noWrap title={value}>
          {value || "—"}
        </Typography>
      ),
    },
    {
      field: "usageCount",
      headerName: "Usage",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Chip label={value ?? 0} size="small" variant="outlined" />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => {
              setEditEntity({
                permissionId: row.permissionId,
                name: row.permissionName,
                key: row.permissionKey,
                description: row.description,
              });
              setFormOpen(true);
            }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
        Permissions cannot be deactivated. They live forever in the catalog.
      </Alert>

      <Box sx={{ display: "flex", mb: 2, alignItems: "center" }}>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditEntity(null);
            setFormOpen(true);
          }}
        >
          New Permission
        </Button>
      </Box>

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.permissionId}
        loading={loading}
        autoHeight
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 50 } },
        }}
        disableRowSelectionOnClick
        density="comfortable"
        sx={{ border: "none" }}
      />

      <CatalogFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditEntity(null);
        }}
        onSave={handleSave}
        entity={editEntity}
        labels={FORM_LABELS}
        loading={saving}
      />

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={closeSnack}>
        <Alert severity={snack.severity} onClose={closeSnack}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PermissionsCatalogTab;
